import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PointsData {
  points: number;
  found: boolean;
  username?: string;
  watchTime?: number;
  rank?: number;
}

export function useKickletPoints() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getPoints = useCallback(async (username: string): Promise<PointsData | null> => {
    if (!username) {
      setError('Username is required');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('kicklet', {
        body: { action: 'get_points', username }
      });

      if (fnError) {
        setError(fnError.message);
        return null;
      }

      if (data.error) {
        setError(data.error);
        return null;
      }

      return data as PointsData;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch points');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const removePoints = useCallback(async (username: string, points: number): Promise<boolean> => {
    if (!username || points <= 0) {
      setError('Invalid username or points');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('kicklet', {
        body: { action: 'remove_points', username, points }
      });

      if (fnError) {
        setError(fnError.message);
        return false;
      }

      if (data.error) {
        setError(data.error);
        return false;
      }

      return data.success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove points');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const addPoints = useCallback(async (username: string, points: number): Promise<boolean> => {
    if (!username || points <= 0) {
      setError('Invalid username or points');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('kicklet', {
        body: { action: 'add_points', username, points }
      });

      if (fnError) {
        setError(fnError.message);
        return false;
      }

      if (data.error) {
        setError(data.error);
        return false;
      }

      return data.success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add points');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return { getPoints, removePoints, addPoints, loading, error };
}
